/**
 * WhatsApp picture intake — the words the dashboard shows for a consultation
 * form photographed on WhatsApp (2026-10-14): where a form is, who sent it,
 * who ended it, and what a filing on the Members page did.
 *
 * Pure and dependency-light (type-only imports), so `picture-intakes.test.ts`
 * pins every sentence without React or the Supabase client.
 */

import type { MemberDetailConsultationForm } from "./api";

/** Who sent a form, as a phrase: "the member", "Sam", or "a staff member". */
function senderPhrase(form: Pick<MemberDetailConsultationForm, "sender_kind" | "sender_staff_name">): string {
  if (form.sender_kind === "member") return "the member";
  return form.sender_staff_name ?? "a staff member";
}

/** Where a form is, for the member page's list. */
export function consultationFormStatusLabel(
  form: Pick<MemberDetailConsultationForm, "status" | "sender_kind" | "sender_staff_name" | "failed_reason">,
): string {
  switch (form.status) {
    case "extracting":
      return "Being read";
    case "awaiting_match":
      return "Waiting for a member to be chosen";
    case "awaiting_confirmation":
      return `Waiting for ${senderPhrase(form)} to confirm on WhatsApp`;
    case "awaiting_correction":
      return `Waiting for ${senderPhrase(form)}'s correction on WhatsApp`;
    case "confirmed":
      return "Saved to the profile";
    case "discarded":
      return "Discarded";
    case "expired":
      return "Expired unanswered";
    case "failed":
      return form.failed_reason === "form_unreadable"
        ? "Couldn't be read"
        : form.failed_reason === "photo_missing"
          ? "The photo couldn't be kept"
          : "Stopped before it was saved";
    default:
      return form.status;
  }
}

/** "Sent by the member on WhatsApp" / "Sent by Sam on WhatsApp". */
export function consultationFormSentLine(
  form: Pick<MemberDetailConsultationForm, "sender_kind" | "sender_staff_name">,
): string {
  return `Sent by ${senderPhrase(form)} on WhatsApp`;
}

/** Who ended a form and where — `null` while it is open. */
export function consultationFormEndedLine(
  form: Pick<MemberDetailConsultationForm, "status" | "ended_by">,
): string | null {
  const by = form.ended_by;
  if (by === null) return null;
  const verb =
    form.status === "discarded" ? "Discarded" : form.status === "confirmed" ? "Confirmed" : "Answered";
  const who =
    by.kind === "member"
      ? "the member"
      : by.kind === "staff"
        ? (by.staff_name ?? "a staff member")
        : by.kind === "cli"
          ? "the operator's CLI"
          : "the system";
  const where =
    by.via === "whatsapp" ? " on WhatsApp" : by.via === "dashboard" ? " on the dashboard" : "";
  return `${verb} by ${who}${where}`;
}

/** How long until an unfiled form (and its photo) is deleted. */
export function waitingFormExpiryLabel(expiresAt: string, now: number): string {
  const ms = Date.parse(expiresAt) - now;
  if (!Number.isFinite(ms) || ms <= 0) return "Being deleted";
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return "Deleted today unless filed";
  return `Deleted in ${days} day${days === 1 ? "" : "s"} unless filed`;
}

const CANDIDATE_REASON_WORDS: Readonly<Record<string, string>> = {
  name: "name",
  postcode: "postcode",
  booked_today: "booked today",
  phone: "phone",
  email: "email",
};

/** Why the matcher put a member on the list: "name · postcode". */
export function candidateReasonsLabel(reasons: ReadonlyArray<string>): string {
  return reasons.map((r) => CANDIDATE_REASON_WORDS[r] ?? r).join(" · ");
}

/**
 * What a filing or a discard on the Members page did, as a sentence.
 * `photoRemoved` is the discard's own answer: `false` when the form is gone
 * but its photo is still in Storage (a failed removal the retention sweeper
 * clears), so the page never says a photo was deleted when it was not.
 */
export function waitingFormOutcomeMessage(
  outcome: string,
  memberLabel: string | null,
  photoRemoved: boolean | null = null,
): { readonly ok: boolean; readonly text: string } {
  switch (outcome) {
    case "confirmed":
      return { ok: true, text: `Saved to ${memberLabel ?? "the member"}'s profile.` };
    case "discarded":
      return photoRemoved === false
        ? {
            ok: true,
            text: "Form deleted. Its photo couldn't be removed just now — the daily clean-up will delete it.",
          }
        : { ok: true, text: "Form and photo deleted." };
    case "already_confirmed":
      return { ok: false, text: "Someone has already filed this form." };
    case "already_discarded":
      return { ok: false, text: "This form has already been discarded." };
    case "not_waiting":
      return {
        ok: false,
        text: "This form is being answered on WhatsApp now — a member was chosen there.",
      };
    case "expired":
      return { ok: false, text: "This form expired and is being deleted." };
    case "closed":
      return { ok: false, text: "This form was stopped and can't be filed." };
    case "member_gone":
      return { ok: false, text: "That member has been deleted. Choose another." };
    case "not_found":
      return { ok: false, text: "This form is no longer there." };
    default:
      return { ok: false, text: `Not saved (${outcome}).` };
  }
}
